import React, { useCallback, useReducer } from 'react';
import { node, object, func, oneOfType } from 'prop-types';

import { deepClone , noop } from '@ajaibid/utils';
import reducers, {
  CREATE_FIELD,
  UPDATE_FIELD,
  DELETE_FIELD,
  RESET_FORM,
  UPDATE_FORM,
} from './reducers';
import { validateField } from './helper';

const FormContext = React.createContext();

const FormProvider = (props) => {
  const { children, initialValues, onSubmit, onFieldsChange } = props;
  const [state, dispatch] = useReducer(reducers, {
    fields: initialValues,
    hasFieldsChanged: false,
  });

  const onFieldsChangeCb = useCallback((param) => onFieldsChange(param), [onFieldsChange]);

  const dispatchHelper = useCallback((type, payload) => {
    dispatch({ type, payload });
  }, []);

  const handleGetField = useCallback(
    (field) => {
      if (state.fields?.[field]) {
        return state.fields[field];
      }

      return {};
    },
    [state]
  );

  const handleCreateField = useCallback(
    (field = '', value = '', validators = {}) => {
      if (field) {
        dispatchHelper(CREATE_FIELD, { field, value, validators });
      }
    },
    [dispatchHelper]
  );

  const handleUpdateField = useCallback(
    (field = '', value = '', validators) => {
      if (field) {
        dispatchHelper(UPDATE_FIELD, { field, value, validators });
        onFieldsChangeCb({ field, value });
      }
    },
    [dispatchHelper, onFieldsChangeCb]
  );

  const handleDeleteField = useCallback(
    (field) => {
      if (field) {
        dispatchHelper(DELETE_FIELD, { field });
      }
    },
    [dispatchHelper]
  );

  const handleResetForm = useCallback(() => {
    dispatchHelper(RESET_FORM, { valuesObj: initialValues });
  }, [dispatchHelper, initialValues]);

  const handleValidateForm = useCallback(async () => {
    const newValues = deepClone(state.fields);

    Object.keys(newValues).forEach((i) => {
      const errorData = validateField(newValues[i].value, newValues[i].validators);
      newValues[i].error = errorData.errorMessage;
      newValues[i].errorKey = errorData.errorKey;
    });

    dispatchHelper(UPDATE_FORM, { valuesObj: newValues });
    return Promise.resolve(newValues);
  }, [JSON.stringify(state.fields)]);
  // FIXME: state.fields.*.validators has a function. If the func changes, the dependency
  // will not identify as JSON.stringify() ignores any functions.

  const handleSubmit = useCallback(() => {
    onSubmit(state.fields, { getField: handleGetField });
  }, [state, onSubmit, handleGetField, dispatchHelper]);

  return (
    <FormContext.Provider
      value={[
        state.fields,
        {
          createField: handleCreateField,
          updateField: handleUpdateField,
          deleteField: handleDeleteField,
          getField: handleGetField,
          resetForm: handleResetForm,
          validateForm: handleValidateForm,
          submitForm: handleSubmit,
        },
      ]}
    >
      {children}
    </FormContext.Provider>
  );
};

FormProvider.propTypes = {
  children: oneOfType([node, func]),
  initialValues: object,
  onFieldsChange: func,
  onSubmit: func,
};

FormProvider.defaultProps = {
  children: null,
  initialValues: {},
  onFieldsChange: noop,
  onSubmit: noop,
};

export { FormProvider as default, FormContext };
